import { Key } from "ts-keycode-enum";

const alphanumericWithSpecialCharsDirective = {
    created(element: HTMLInputElement) {
        if (!!element) {
            element.onkeydown = (e) => {
                return checkIfEnteredKeyIsAlphanumericWithSpecialChars(e);
            };

            element.oninput = () => {
                sanitizeToAlphanumericWithSpecialChars(element);
            };

            element.onpaste = (e) => {
                const pastedText = e.clipboardData?.getData("text") ?? "";
                const sanitizedPastedText = sanitizeTextToAlphanumericWithSpecialChars(pastedText);

                if (pastedText !== sanitizedPastedText) {
                    e.preventDefault();

                    const selectionStart = element.selectionStart ?? element.value.length;
                    const selectionEnd = element.selectionEnd ?? element.value.length;
                    const newValue =
                        element.value.substring(0, selectionStart) +
                        sanitizedPastedText +
                        element.value.substring(selectionEnd);

                    element.value = sanitizeTextToAlphanumericWithSpecialChars(newValue);
                    const cursorPosition = selectionStart + sanitizedPastedText.length;
                    element.setSelectionRange(cursorPosition, cursorPosition);
                    element.dispatchEvent(new Event("input", { bubbles: true }));
                }
            };
        }
    }
};

const alphanumericWithDashAndSpaceDirective = {
    created(element: HTMLInputElement) {
        if (!!element) {
            element.onkeydown = (e) => {
                return checkIfEnteredKeyIsAlphanumericWithDashAndSpace(e);
            };

            element.oninput = () => {
                sanitizeToAlphanumericDashAndSpace(element);
            };

            element.onpaste = (e) => {
                const pastedText = e.clipboardData?.getData("text") ?? "";
                const sanitizedPastedText = sanitizeTextToAlphanumericDashAndSpace(pastedText);

                if (pastedText !== sanitizedPastedText) {
                    e.preventDefault();

                    const selectionStart = element.selectionStart ?? element.value.length;
                    const selectionEnd = element.selectionEnd ?? element.value.length;
                    const newValue =
                        element.value.substring(0, selectionStart) +
                        sanitizedPastedText +
                        element.value.substring(selectionEnd);

                    element.value = sanitizeTextToAlphanumericDashAndSpace(newValue);
                    const cursorPosition = selectionStart + sanitizedPastedText.length;
                    element.setSelectionRange(cursorPosition, cursorPosition);
                    element.dispatchEvent(new Event("input", { bubbles: true }));
                }
            };
        }
    }
};

function checkIfEnteredKeyIsAlphanumericWithSpecialChars(e: KeyboardEvent): boolean {
    const keyCode = e.keyCode || e.which;

    const cutCopyPaste = e.ctrlKey && (keyCode === Key.C || keyCode === Key.V || keyCode === Key.X);
    if (cutCopyPaste) return true;

    const isLetter = keyCode >= Key.A && keyCode <= Key.Z;
    const isDigit =
        (keyCode >= Key.Zero && keyCode <= Key.Nine && !e.shiftKey) ||
        (keyCode >= Key.Numpad0 && keyCode <= Key.Numpad9);

    // Allowed special characters: -, ,, /, _, :, $
    const isDashOrUnderscore = keyCode === Key.Dash || keyCode === Key.Subtract;
    const isForwardSlash = (keyCode === Key.ForwardSlash && !e.shiftKey) || keyCode === Key.Divide;
    const isColon = keyCode === Key.SemiColon && e.shiftKey;
    const isComma = keyCode === Key.Comma && !e.shiftKey;
    const isDollar = keyCode === 52 && e.shiftKey;

    const isNavigationOrEditingKey =
        keyCode === Key.Backspace ||
        keyCode === Key.Delete ||
        keyCode === Key.LeftArrow ||
        keyCode === Key.RightArrow ||
        keyCode === Key.UpArrow ||
        keyCode === Key.DownArrow ||
        keyCode === Key.Tab ||
        keyCode === Key.Space;

    if (
        isLetter ||
        isDigit ||
        isDashOrUnderscore ||
        isForwardSlash ||
        isColon ||
        isComma ||
        isDollar ||
        isNavigationOrEditingKey
    ) {
        return true;
    }

    return false;
}

function checkIfEnteredKeyIsAlphanumericWithDashAndSpace(e: KeyboardEvent): boolean {
    const keyCode = e.keyCode || e.which;

    const shortcutKeys =
        (e.ctrlKey || e.metaKey) &&
        (keyCode === Key.C ||
            keyCode === Key.V ||
            keyCode === Key.X ||
            keyCode === Key.A ||
            keyCode === Key.Z ||
            keyCode === Key.Y);
    if (shortcutKeys) return true;

    const isLetter = keyCode >= Key.A && keyCode <= Key.Z;
    const isDigit =
        (keyCode >= Key.Zero && keyCode <= Key.Nine && !e.shiftKey) ||
        (keyCode >= Key.Numpad0 && keyCode <= Key.Numpad9);

    // Allowed characters for this function: letters, numbers (including numpad), dash and space.
    const isDash = keyCode === Key.Dash || keyCode === Key.Subtract;
    const isNavigationOrEditingKey =
        keyCode === Key.Backspace ||
        keyCode === Key.Delete ||
        keyCode === Key.LeftArrow ||
        keyCode === Key.RightArrow ||
        keyCode === Key.UpArrow ||
        keyCode === Key.DownArrow ||
        keyCode === Key.Tab ||
        keyCode === Key.Space ||
        keyCode === Key.Home ||
        keyCode === Key.End;

    if (isLetter || isDigit || isDash || isNavigationOrEditingKey) {
        return true;
    }

    return false;
}

// Allowed special characters: -, ,, /, _, :, $
function sanitizeTextToAlphanumericWithSpecialChars(value: string): string {
    return value.replace(/[^A-Za-z0-9\-,/_:$ ]+/g, "");
}

function sanitizeToAlphanumericWithSpecialChars(element: HTMLInputElement): void {
    const sanitizedValue = sanitizeTextToAlphanumericWithSpecialChars(element.value);
    if (sanitizedValue !== element.value) {
        element.value = sanitizedValue;
        element.dispatchEvent(new Event("input", { bubbles: true }));
    }
}

function sanitizeTextToAlphanumericDashAndSpace(value: string): string {
    return value.replace(/[^A-Za-z0-9\- ]+/g, "");
}

function sanitizeToAlphanumericDashAndSpace(element: HTMLInputElement): void {
    const sanitizedValue = sanitizeTextToAlphanumericDashAndSpace(element.value);
    if (sanitizedValue !== element.value) {
        element.value = sanitizedValue;
        element.dispatchEvent(new Event("input", { bubbles: true }));
    }
}

export const alphanumericWithSpecialCharsDirectives = {
    "alphanumeric-special-only": alphanumericWithSpecialCharsDirective,
    "alphanumeric-dash-space-only": alphanumericWithDashAndSpaceDirective
};
